import { useEffect, useRef } from 'react';
import L from 'leaflet';
import { circle as turfCircle, point as turfPoint, booleanPointInPolygon } from '@turf/turf';
import {
  MILES_TO_METERS,
  AMENITY_CATEGORIES,
  AMENITY_CATEGORY_BY_DB,
  PROPERTY_COLOR_DEFAULT,
  PROPERTY_COLOR_IN_RADIUS,
  PROPERTY_STROKE,
  type AmenityCategory,
} from '@/lib/map-constants';
import type {
  MapAmenity,
  MapDataResponse,
  MapProperty,
  RankedPropertyDTO,
} from '@/types/db';

export interface LeafletMapProps {
  mapData: MapDataResponse;
  /** Current radius in miles. Default 0.5. Plan 08-02 wires from useFilters. */
  radiusMiles?: number;
  /** Set of category DB names to render. Default = all 4. Plan 08-02 wires from useFilters. */
  visibleCategories?: ReadonlySet<AmenityCategory>;
  /** Whether in-radius properties get the lit/larger styling. Default true. */
  highlightInRadius?: boolean;
  /**
   * Plan 260502-08r — when true, properties whose center is OUTSIDE the
   * radius circle of every visible amenity are NOT rendered as markers
   * (sister filter to highlightInRadius which only restyles). Default false
   * — first-time visitors see all properties; the user opts in via the
   * RadiusOnlyToggle chip in <MapFilterBar>.
   */
  showOnlyInRadius?: boolean;
  /**
   * Phase 08-r — when true, render the amenity radius rings on the map.
   * Default `false` (cleaner default — 539 amenities × overlapping radii is
   * visually noisy unless the user opts in). Independent of the in/out-of-
   * radius classification (which always runs to drive the highlight + filter
   * toggles); this prop only controls whether the geometry is *visible*.
   */
  showRadiusCircles?: boolean;
  /**
   * Phase 08-r — when true, render the amenity marker pins. Default `false`
   * per user spec ("I should never see a pin"). Classification still runs;
   * pin visibility is purely cosmetic.
   */
  showAmenityPins?: boolean;
  /**
   * Composite + neighbor-count rows by property id. Sourced from
   * useMapRankings(filters.radius) in <MapPage>. When undefined or
   * missing an entry, the popup composite badge shows '—' (graceful
   * degradation — popup STILL renders address + counts + link).
   *
   * Plan 08-04 (gap closure for ROADMAP § Phase 08 SC #4).
   */
  rankings?: ReadonlyMap<number, RankedPropertyDTO>;
}

/**
 * Context passed to `buildPropertyPopup` so the popup can render the
 * composite badge + per-visible-category counts. The context is rebuilt
 * per popup-render call so radius/category changes are reflected.
 *
 * Plan 08-04 (gap closure).
 */
export interface PopupContext {
  rankings?: ReadonlyMap<number, RankedPropertyDTO>;
  visibleCategories: ReadonlySet<AmenityCategory>;
  amenities: ReadonlyArray<MapAmenity>;
  radiusMiles: number;
}

interface PropertyState {
  data: MapProperty;
  latLng: L.LatLng;
  marker: L.CircleMarker;
  inRadius: boolean;
}

interface AmenityState {
  data: MapAmenity;
  latLng: L.LatLng;
  marker: L.Marker;
  circle: L.Circle;
}

/**
 * `<LeafletMap>` — plain Leaflet 1.9.4 imperatively wrapped in React.
 *
 * Pattern: single useEffect on a ref'd <div>. `react-leaflet` is explicitly
 * forbidden (CONTEXT.md decision; Pitfall 14 reasoning). Subsequent prop
 * changes (radius, visibleCategories, highlightInRadius) flow through
 * narrower useEffects that mutate Leaflet objects directly — no React
 * reconciliation of map state.
 *
 * Renderer: `L.canvas()` shared instance for ALL circleMarker properties +
 * amenity radius L.circle objects (553+67=620 nodes — SVG would jank).
 */
export function LeafletMap({
  mapData,
  radiusMiles = 0.5,
  visibleCategories,
  highlightInRadius = true,
  showOnlyInRadius = false,
  showRadiusCircles = false,
  showAmenityPins = false,
  rankings,
}: LeafletMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const canvasRendererRef = useRef<L.Renderer | null>(null);
  const propertiesRef = useRef<PropertyState[]>([]);
  const amenitiesRef = useRef<AmenityState[]>([]);
  const amenitiesByCategoryRef = useRef<Record<string, AmenityState[]>>({});

  // Effective visible-set: defaults to ALL 4 categories.
  const effectiveVisible = visibleCategories ?? new Set<AmenityCategory>(
    AMENITY_CATEGORIES.map((c) => c.db),
  );

  // Plan 08-04: live-mutable popup context — refs so we can call
  // setPopupContent on any open popup when filters change (v1.0 invariant
  // from web/public/map.js:374-379). Updated by the popup-refresh effect
  // every render that mutates rankings/radius/visibleCategories/amenities.
  const popupCtxRef = useRef<PopupContext>({
    rankings,
    visibleCategories: effectiveVisible,
    amenities: mapData.amenities,
    radiusMiles,
  });
  const openPropertyPopupIdRef = useRef<number | null>(null);

  // ===================================================================
  // Init effect — runs ONCE on mount when both ref + data are ready.
  // Cleanup tears down the map fully.
  // ===================================================================
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const canvasRenderer = L.canvas();
    canvasRendererRef.current = canvasRenderer;

    const map = L.map(containerRef.current, {
      renderer: canvasRenderer,
      zoomControl: true,
      attributionControl: true,
      // role="application" added in JSX; Leaflet container picks it up.
    });

    // Phase 08-r — Glass Coast dark theme. CartoDB Dark Matter is a free,
    // no-key OSM-data tile layer that matches the SPA's dark aesthetic. The
    // {r} placeholder expands to "@2x" on hi-DPI displays for crisper tiles.
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 20,
    }).addTo(map);

    // Phase 08-r — Initial view spans Clearwater (NW) → Miami (SE), naturally
    // centering on Orlando-ish FL. Replaces the prior hardcoded St. Pete center.
    // The amenity/property auto-fit below this block will override on data
    // load; this is the pre-data fallback view.
    map.fitBounds(
      [
        [27.97, -82.79], // NW corner — Clearwater
        [25.76, -80.19], // SE corner — Miami
      ],
      { padding: [20, 20] },
    );

    mapRef.current = map;

    // ---------- populate properties ----------
    const properties: PropertyState[] = [];
    for (const p of mapData.properties) {
      if (p.lat == null || p.lon == null) continue;
      const latLng = L.latLng(p.lat, p.lon);
      const marker = L.circleMarker(latLng, {
        renderer: canvasRenderer,
        radius: 8,
        color: PROPERTY_STROKE,
        weight: 2,
        fillColor: PROPERTY_COLOR_DEFAULT,
        fillOpacity: 0.9,
      });
      marker.bindTooltip(p.address);
      marker.bindPopup(() => buildPropertyPopup(p, popupCtxRef.current), {
        maxWidth: 300,
      });
      marker.on('popupopen', () => {
        openPropertyPopupIdRef.current = p.id;
      });
      marker.on('popupclose', () => {
        if (openPropertyPopupIdRef.current === p.id) {
          openPropertyPopupIdRef.current = null;
        }
      });
      marker.addTo(map);
      properties.push({ data: p, latLng, marker, inRadius: false });
    }
    propertiesRef.current = properties;

    // ---------- populate amenities ----------
    const amenities: AmenityState[] = [];
    const byCategory: Record<string, AmenityState[]> = {};
    const initialRadiusMeters = radiusMiles * MILES_TO_METERS;
    for (const a of mapData.amenities) {
      if (a.lat == null || a.lon == null) continue;
      // Defensive — bad data has been observed (lon=-8130136 from a malformed
      // import). Without this guard the auto-fit blows the bounds to span the
      // globe and every marker becomes a sub-pixel dot.
      if (a.lat < -90 || a.lat > 90 || a.lon < -180 || a.lon > 180) continue;
      const meta = AMENITY_CATEGORY_BY_DB[a.category];
      if (!meta) continue; // unknown category — skip defensively
      const latLng = L.latLng(a.lat, a.lon);

      const marker = L.marker(latLng, {
        icon: L.divIcon({
          className: '', // strips Leaflet's default white-box background (v1.0 fix)
          html: `<div class="amenity-pin amenity-pin--${meta.slug}"></div>`,
          iconSize: [16, 16],
          iconAnchor: [8, 8],
        }),
      });
      marker.bindTooltip(a.name);
      marker.bindPopup(() => buildAmenityPopup(a), { maxWidth: 280 });

      const circle = L.circle(latLng, {
        renderer: canvasRenderer,
        radius: initialRadiusMeters,
        color: meta.color,
        weight: 1.5,
        opacity: 0.6,
        fillColor: meta.color,
        fillOpacity: 0.12,
        interactive: false, // clicks pass through to underlying property markers
      });

      // Phase 08-r — pins are off by default. The visible-categories effect
      // below adds them when both `showAmenityPins` and the category are on.
      if (showAmenityPins) marker.addTo(map);
      // Same for radius circles.
      if (showRadiusCircles) circle.addTo(map);

      const state: AmenityState = { data: a, latLng, marker, circle };
      amenities.push(state);
      const bucket = byCategory[a.category] ?? (byCategory[a.category] = []);
      bucket.push(state);
    }
    amenitiesRef.current = amenities;
    amenitiesByCategoryRef.current = byCategory;

    // Phase 08-r — Auto-fit removed. Initial view is the FL bounds set above
    // (Clearwater→Miami, Orlando-centered). The user explicitly asked for the
    // FL view to stick on first load instead of zooming into wherever the data
    // happens to cluster. Auto-fit was also fragile against bad-coord data
    // (e.g. one malformed amenity with lon=-8130136 blew the bounds to span
    // the globe; the lat/lon range guard above mitigates that as well).

    // ---------- initial highlights pass ----------
    recalculateHighlights({
      map,
      properties,
      amenitiesByCategory: byCategory,
      visibleCategories: effectiveVisible,
      radiusMiles,
      highlightInRadius,
      showOnlyInRadius,
    });

    // ---------- mobile sizing robustness ----------
    // Leaflet caches the container pixel size at init and re-reads it on the
    // window 'resize' event (trackResize). But this map lives in a flex/dvh
    // column: the mobile filter disclosure expanding/collapsing — and mobile
    // browser-chrome show/hide changing 100dvh — resize the container WITHOUT
    // firing a window resize, so Leaflet would keep a stale size (gray strip).
    // A ResizeObserver re-syncs via invalidateSize (rAF-debounced so a burst
    // of layout changes collapses into a single redraw).
    let invalidateRaf = 0;
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            cancelAnimationFrame(invalidateRaf);
            invalidateRaf = requestAnimationFrame(() => {
              mapRef.current?.invalidateSize();
            });
          })
        : null;
    if (resizeObserver && containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    // One post-layout pass — the container can settle a frame after mount.
    const initialInvalidateRaf = requestAnimationFrame(() => {
      mapRef.current?.invalidateSize();
    });

    return () => {
      map.off();
      map.remove();
      mapRef.current = null;
      canvasRendererRef.current = null;
      propertiesRef.current = [];
      amenitiesRef.current = [];
      amenitiesByCategoryRef.current = {};
      openPropertyPopupIdRef.current = null;
      if (resizeObserver) resizeObserver.disconnect();
      cancelAnimationFrame(invalidateRaf);
      cancelAnimationFrame(initialInvalidateRaf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapData]); // re-init only on full data refresh

  // ===================================================================
  // Radius effect — setRadius on every amenity circle + recalc highlights
  // ===================================================================
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const meters = radiusMiles * MILES_TO_METERS;
    for (const a of amenitiesRef.current) {
      a.circle.setRadius(meters);
    }
    recalculateHighlights({
      map,
      properties: propertiesRef.current,
      amenitiesByCategory: amenitiesByCategoryRef.current,
      visibleCategories: effectiveVisible,
      radiusMiles,
      highlightInRadius,
      showOnlyInRadius,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radiusMiles]);

  // ===================================================================
  // Visible-categories effect — add/remove markers + circles per category.
  // Phase 08-r: circles also gated on `showRadiusCircles`. Marker visibility
  // is independent (markers always show when their category is on).
  // ===================================================================
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    for (const cat of AMENITY_CATEGORIES) {
      const items = amenitiesByCategoryRef.current[cat.db] ?? [];
      const catVisible = effectiveVisible.has(cat.db);
      const pinVisible = catVisible && showAmenityPins;
      const circleVisible = catVisible && showRadiusCircles;
      for (const a of items) {
        if (pinVisible) {
          if (!map.hasLayer(a.marker)) a.marker.addTo(map);
        } else {
          if (map.hasLayer(a.marker)) map.removeLayer(a.marker);
        }
        if (circleVisible) {
          if (!map.hasLayer(a.circle)) a.circle.addTo(map);
        } else {
          if (map.hasLayer(a.circle)) map.removeLayer(a.circle);
        }
      }
    }
    recalculateHighlights({
      map,
      properties: propertiesRef.current,
      amenitiesByCategory: amenitiesByCategoryRef.current,
      visibleCategories: effectiveVisible,
      radiusMiles,
      highlightInRadius,
      showOnlyInRadius,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Array.from(effectiveVisible).sort().join(','), showRadiusCircles, showAmenityPins]);

  // ===================================================================
  // Highlight-toggle / show-only-in-radius effect — re-runs the
  // classification with the new flag(s). Both restyling (highlightInRadius)
  // and visibility (showOnlyInRadius) are owned by recalculateHighlights so
  // they share a single dependency-driven re-render path.
  // ===================================================================
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    recalculateHighlights({
      map,
      properties: propertiesRef.current,
      amenitiesByCategory: amenitiesByCategoryRef.current,
      visibleCategories: effectiveVisible,
      radiusMiles,
      highlightInRadius,
      showOnlyInRadius,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightInRadius, showOnlyInRadius]);

  // ===================================================================
  // Popup-refresh effect — keeps popupCtxRef in sync AND refreshes any
  // open property popup so composite + per-category counts reflect the
  // user's current filter state (Plan 08-04 — v1.0 invariant; see
  // web/public/map.js lines 374-379 for the original pattern).
  // ===================================================================
  useEffect(() => {
    popupCtxRef.current = {
      rankings,
      visibleCategories: effectiveVisible,
      amenities: mapData.amenities,
      radiusMiles,
    };
    const openId = openPropertyPopupIdRef.current;
    if (openId == null) return;
    const openProp = propertiesRef.current.find((p) => p.data.id === openId);
    if (!openProp) return;
    if (
      typeof openProp.marker.isPopupOpen === 'function' &&
      openProp.marker.isPopupOpen()
    ) {
      openProp.marker.setPopupContent(
        buildPropertyPopup(openProp.data, popupCtxRef.current),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rankings,
    radiusMiles,
    Array.from(effectiveVisible).sort().join(','),
    mapData.amenities,
  ]);

  return (
    <div
      ref={containerRef}
      className="leaflet-map-root w-full h-full"
      role="application"
      aria-label="Interactive map of Florida properties and dog amenities"
    />
  );
}

// ---------------------------------------------------------------------
// Helper: classifyInRadius — pure Turf.js point-in-polygon classification
// (MAP-03 contract). Exported for testability (Plan 08-03 parity test).
//
// Given a property + amenity list + radius, returns a Map<propertyId,
// boolean> where `true` means the property falls inside ≥1 visible
// amenity's circle. No Leaflet side-effects.
// ---------------------------------------------------------------------
export function classifyInRadius(
  properties: ReadonlyArray<MapProperty>,
  amenities: ReadonlyArray<MapAmenity>,
  visibleCategories: ReadonlySet<AmenityCategory>,
  radiusMiles: number,
): Map<number, boolean> {
  const result = new Map<number, boolean>();
  const visibleAmenities = amenities.filter(
    (a) =>
      a.lat != null &&
      a.lon != null &&
      visibleCategories.has(a.category as AmenityCategory),
  );
  const polygons = visibleAmenities.map((a) =>
    turfCircle([a.lon as number, a.lat as number], radiusMiles, {
      units: 'miles',
      steps: 32,
    }),
  );
  for (const p of properties) {
    if (p.lat == null || p.lon == null) {
      result.set(p.id, false);
      continue;
    }
    const pt = turfPoint([p.lon, p.lat]);
    let covered = false;
    for (const poly of polygons) {
      if (booleanPointInPolygon(pt, poly)) {
        covered = true;
        break;
      }
    }
    result.set(p.id, covered);
  }
  return result;
}

// ---------------------------------------------------------------------
// Helper: recalculateHighlights — Turf.js point-in-polygon classification
// (MAP-03 contract).
//
// Plan 260502-08r — also owns marker visibility when `showOnlyInRadius` is
// true: out-of-radius properties are removed from the map (not destroyed —
// the marker objects live on in propertiesRef so toggling back to false
// re-adds them without re-allocation). When `showOnlyInRadius` is false,
// every property marker is ensured to be on the map.
// ---------------------------------------------------------------------
interface RecalcOpts {
  /** Leaflet map instance — used to add/remove markers per `showOnlyInRadius`. */
  map: L.Map;
  properties: PropertyState[];
  amenitiesByCategory: Record<string, AmenityState[]>;
  visibleCategories: ReadonlySet<AmenityCategory>;
  radiusMiles: number;
  highlightInRadius: boolean;
  /** Plan 260502-08r — when true, hide out-of-radius properties entirely. */
  showOnlyInRadius: boolean;
}

function recalculateHighlights(opts: RecalcOpts) {
  const {
    map,
    properties,
    amenitiesByCategory,
    visibleCategories,
    radiusMiles,
    highlightInRadius,
    showOnlyInRadius,
  } = opts;

  // Build flat list of currently-visible amenities.
  const visibleAmenities: AmenityState[] = [];
  for (const cat of visibleCategories) {
    const arr = amenitiesByCategory[cat] ?? [];
    for (const a of arr) visibleAmenities.push(a);
  }

  // Pre-build Turf circle polygons (32 steps — geodesic) for every visible amenity.
  // We trade memory (553 polygons) for clarity over hand-rolled Haversine —
  // MAP-03 explicitly requires Turf.js.
  const turfPolygons = visibleAmenities.map((a) =>
    turfCircle([a.data.lon ?? 0, a.data.lat ?? 0], radiusMiles, { units: 'miles', steps: 32 }),
  );

  for (const p of properties) {
    if (p.data.lat == null || p.data.lon == null) continue;
    const pt = turfPoint([p.data.lon, p.data.lat]);
    let covered = false;
    for (const poly of turfPolygons) {
      if (booleanPointInPolygon(pt, poly)) {
        covered = true;
        break;
      }
    }
    p.inRadius = covered;

    // Plan 260502-08r — visibility toggle. The classification (`covered`) is
    // computed first so the styling pass below sees a consistent inRadius
    // even for currently-hidden markers.
    const shouldShow = !showOnlyInRadius || covered;
    if (shouldShow) {
      if (!map.hasLayer(p.marker)) p.marker.addTo(map);
    } else {
      if (map.hasLayer(p.marker)) map.removeLayer(p.marker);
    }

    const lit = highlightInRadius && covered;
    p.marker.setStyle({
      fillColor: lit ? PROPERTY_COLOR_IN_RADIUS : PROPERTY_COLOR_DEFAULT,
    });
    p.marker.setRadius(lit ? 10 : 8);
  }
}

// ---------------------------------------------------------------------
// Helper: composeColorClass — Plan 08-04 tier rule mirror.
//
// Matches RankingBadge.tsx:45 (Plan 05-04). Keeps the popup composite
// numeral on the same reserved-list slot #3 as the dashboard cards. When
// score is undefined (rankings query missing the row), returns the dim/
// low-tier class so the popup degrades gracefully.
// ---------------------------------------------------------------------
function composeColorClass(score: number | undefined): string {
  if (score == null) return 'text-foam-50/70';
  if (score >= 80) return 'text-bronze-500';
  if (score >= 50) return 'text-foam-50';
  return 'text-foam-50/70';
}

/**
 * Property popup factory — ROADMAP § Phase 08 SC #4 contract (Plan 08-04
 * gap closure).
 *
 * Emits (in order, all via DOM API — XSS-safe per T-08-01 / T-08-10):
 *   1. Address heading
 *   2. Composite score badge — mono numeral 0–100, tier-colored:
 *        ≥80 → bronze-500 (slot #3 — high-fit signal, RankingBadge mirror)
 *        50–79 → foam-50
 *        <50 → foam-50/70 (dim)
 *      Missing rankings entry → '—' placeholder, dim color (graceful
 *      degradation when /api/rankings is loading or errored).
 *   3. One row per visible category: `${n} ${meta.singular|plural} within ${R} mi`
 *      Counts computed via Turf.js booleanPointInPolygon (same engine as
 *      classifyInRadius) so popup count cannot drift from map highlight.
 *   4. View detail → link to /app/properties/{id}
 *
 * The factory is a PURE function — no Leaflet refs, no React hooks.
 * Called both from `bindPopup(() => factory(p, popupCtxRef.current))`
 * AND directly by the popup-refresh effect when filters change while a
 * popup is already open. Exported for vitest unit tests in Plan 08-04.
 */
export function buildPropertyPopup(
  property: MapProperty,
  ctx: PopupContext,
): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'map-popup map-popup--property';

  // 1. Address heading.
  const heading = document.createElement('h3');
  heading.className = 'text-base font-semibold text-foam-50';
  heading.textContent = property.address;
  wrap.appendChild(heading);

  // 2. Composite score badge — bronze ≥80, foam 50–79, dim <50.
  // Missing rankings entry → '—' placeholder (graceful degradation).
  const row = ctx.rankings?.get(property.id);
  const scoreBlock = document.createElement('div');
  scoreBlock.className = 'map-popup__score mt-2 flex items-baseline gap-2';
  const numeral = document.createElement('span');
  numeral.className =
    'popup-composite-numeral font-mono text-2xl font-semibold ' +
    composeColorClass(row?.composite);
  numeral.textContent = row ? String(Math.round(row.composite)) : '—';
  const label = document.createElement('span');
  label.className = 'text-xs text-foam-50/60';
  label.textContent = 'of 100';
  scoreBlock.appendChild(numeral);
  scoreBlock.appendChild(label);
  wrap.appendChild(scoreBlock);

  // 3. Per-visible-category count rows — Turf.js point-in-polygon parity
  // with classifyInRadius. Hidden categories produce ZERO rows (not
  // zero-count rows) — matches v1.0 web/public/map.js:394-404.
  const radiusStr = ctx.radiusMiles.toFixed(1);
  // Iterate in the locked AMENITY_CATEGORIES order (parks → beach →
  // daycare → bar) so popup row order is stable.
  for (const meta of AMENITY_CATEGORIES) {
    if (!ctx.visibleCategories.has(meta.db)) continue;
    let n = 0;
    if (property.lat != null && property.lon != null) {
      const pt = turfPoint([property.lon, property.lat]);
      for (const a of ctx.amenities) {
        if (a.category !== meta.db) continue;
        if (a.lat == null || a.lon == null) continue;
        const poly = turfCircle([a.lon, a.lat], ctx.radiusMiles, {
          units: 'miles',
          steps: 32,
        });
        if (booleanPointInPolygon(pt, poly)) n++;
      }
    }
    const noun = n === 1 ? meta.singular : meta.plural;
    const rowEl = document.createElement('div');
    rowEl.className = 'text-sm text-foam-50/80';
    rowEl.textContent = `${n} ${noun} within ${radiusStr} mi`;
    wrap.appendChild(rowEl);
  }

  // 4. View detail → link.
  const link = document.createElement('a');
  link.href = `/app/properties/${property.id}`;
  link.className = 'text-tide-400 underline mt-2 inline-block';
  link.textContent = 'View detail →';
  wrap.appendChild(link);

  return wrap;
}

// ---------------------------------------------------------------------
// Helper: buildAmenityPopup — DOM-API construction (XSS mitigation T-08-01)
// Source URL validated via new URL() before rendering (T-08-02).
// ---------------------------------------------------------------------
function buildAmenityPopup(a: MapAmenity): HTMLElement {
  const meta = AMENITY_CATEGORY_BY_DB[a.category];
  const wrap = document.createElement('div');
  wrap.className = 'map-popup map-popup--amenity';

  const heading = document.createElement('h3');
  heading.className = 'text-base font-semibold text-foam-50';
  heading.textContent = a.name;
  wrap.appendChild(heading);

  // Category badge
  const badge = document.createElement('span');
  badge.className = 'inline-block mt-2 px-2 py-1 rounded text-xs font-semibold text-foam-50';
  badge.style.backgroundColor = meta?.color ?? '#7faec9';
  badge.textContent = meta?.displayName ?? a.category;
  wrap.appendChild(badge);

  if (a.address) {
    const addr = document.createElement('div');
    addr.className = 'text-sm text-foam-50/70 mt-2';
    addr.textContent = a.address;
    wrap.appendChild(addr);
  }

  if (a.source_url) {
    try {
      const u = new URL(a.source_url); // T-08-02: validate before rendering
      const link = document.createElement('a');
      link.href = u.href;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.className = 'block mt-2 text-tide-400 underline';
      link.textContent = u.hostname;
      wrap.appendChild(link);
    } catch {
      // invalid URL — silently skip (no link rendered)
    }
  }

  if (a.last_verified_date) {
    const verified = document.createElement('div');
    verified.className = 'text-sm text-foam-50/60 mt-2';
    verified.textContent = `Verified ${a.last_verified_date}`;
    wrap.appendChild(verified);
  }

  if (a.category === 'beach' && a.seasonal_restriction) {
    const seasonal = document.createElement('div');
    seasonal.className = 'text-sm text-coral-500 mt-2';
    seasonal.textContent = `Seasonal: ${a.seasonal_restriction}`;
    wrap.appendChild(seasonal);
  }

  return wrap;
}
